import React from "react";
import { action, makeObservable, observable } from "mobx";
import { observer } from "mobx-react";

import type { ChatResponseItemSettings } from "@root/models";
import ContextLengthSlider from "../ContextLengthSlider";
import { ToggleFormButton } from "@components/ToggleFormButton";

const WHISPER_LANGUAGES = [
  { code: 'auto', name: 'Auto Detect' },
  { code: 'en', name: 'English' },
  { code: 'zh', name: 'Chinese' },
  { code: 'de', name: 'German' },
  { code: 'es', name: 'Spanish' },
  { code: 'ru', name: 'Russian' },
  { code: 'ko', name: 'Korean' },
  { code: 'fr', name: 'French' },
  { code: 'ja', name: 'Japanese' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'tr', name: 'Turkish' },
  { code: 'pl', name: 'Polish' },
  { code: 'ca', name: 'Catalan' },
  { code: 'nl', name: 'Dutch' },
  { code: 'ar', name: 'Arabic' },
  { code: 'sv', name: 'Swedish' },
  { code: 'it', name: 'Italian' },
  { code: 'id', name: 'Indonesian' },
  { code: 'hi', name: 'Hindi' },
  { code: 'fi', name: 'Finnish' },
  { code: 'vi', name: 'Vietnamese' },
  { code: 'he', name: 'Hebrew' },
  { code: 'uk', name: 'Ukrainian' },
  { code: 'el', name: 'Greek' },
  { code: 'ms', name: 'Malay' },
  { code: 'cs', name: 'Czech' },
  { code: 'ro', name: 'Romanian' },
  { code: 'da', name: 'Danish' },
  { code: 'hu', name: 'Hungarian' },
  { code: 'ta', name: 'Tamil' },
  { code: 'no', name: 'Norwegian' },
  { code: 'th', name: 'Thai' },
  { code: 'ur', name: 'Urdu' },
  { code: 'hr', name: 'Croatian' },
  { code: 'bg', name: 'Bulgarian' },
  { code: 'lt', name: 'Lithuanian' },
  { code: 'la', name: 'Latin' },
  { code: 'mi', name: 'Maori' },
  { code: 'ml', name: 'Malayalam' },
  { code: 'cy', name: 'Welsh' },
  { code: 'sk', name: 'Slovak' },
  { code: 'te', name: 'Telugu' },
  { code: 'fa', name: 'Persian' },
  { code: 'lv', name: 'Latvian' },
  { code: 'bn', name: 'Bengali' },
  { code: 'sr', name: 'Serbian' },
  { code: 'az', name: 'Azerbaijani' },
  { code: 'sl', name: 'Slovenian' },
  { code: 'kn', name: 'Kannada' },
  { code: 'et', name: 'Estonian' },
  { code: 'mk', name: 'Macedonian' },
  { code: 'br', name: 'Breton' },
  { code: 'eu', name: 'Basque' },
  { code: 'is', name: 'Icelandic' },
  { code: 'hy', name: 'Armenian' },
  { code: 'ne', name: 'Nepali' },
  { code: 'mn', name: 'Mongolian' },
  { code: 'bs', name: 'Bosnian' },
  { code: 'kk', name: 'Kazakh' },
  { code: 'sq', name: 'Albanian' },
  { code: 'sw', name: 'Swahili' },
  { code: 'gl', name: 'Galician' },
  { code: 'mr', name: 'Marathi' },
  { code: 'pa', name: 'Punjabi' },
  { code: 'si', name: 'Sinhala' },
  { code: 'km', name: 'Khmer' },
  { code: 'sn', name: 'Shona' },
  { code: 'yo', name: 'Yoruba' },
  { code: 'so', name: 'Somali' },
  { code: 'af', name: 'Afrikaans' },
  { code: 'oc', name: 'Occitan' },
  { code: 'ka', name: 'Georgian' },
  { code: 'be', name: 'Belarusian' },
  { code: 'tg', name: 'Tajik' },
  { code: 'sd', name: 'Sindhi' },
  { code: 'gu', name: 'Gujarati' },
  { code: 'am', name: 'Amharic' },
  { code: 'yi', name: 'Yiddish' },
  { code: 'lo', name: 'Lao' },
  { code: 'uz', name: 'Uzbek' },
  { code: 'fo', name: 'Faroese' },
  { code: 'ht', name: 'Haitian Creole' },
  { code: 'ps', name: 'Pashto' },
  { code: 'tk', name: 'Turkmen' },
  { code: 'nn', name: 'Nynorsk' },
  { code: 'mt', name: 'Maltese' },
  { code: 'sa', name: 'Sanskrit' },
  { code: 'lb', name: 'Luxembourgish' },
  { code: 'my', name: 'Myanmar' },
  { code: 'bo', name: 'Tibetan' },
  { code: 'tl', name: 'Tagalog' },
  { code: 'mg', name: 'Malagasy' },
  { code: 'as', name: 'Assamese' },
  { code: 'tt', name: 'Tatar' },
  { code: 'haw', name: 'Hawaiian' },
  { code: 'ln', name: 'Lingala' },
  { code: 'ha', name: 'Hausa' },
  { code: 'ba', name: 'Bashkir' },
  { code: 'jw', name: 'Javanese' },
  { code: 'su', name: 'Sundanese' },
];

type ChatConfigTabProps = {
  chat: ChatResponseItemSettings;
  onSettingsChanged: (settings: ChatResponseItemSettings) => void;
};

const PREDICT_OPTIONS = ['1k', '2k', '4k', '8k', '12k', '16k', '20k', '32k', 'auto'];

@observer
class ChatConfigTab extends React.Component<ChatConfigTabProps> {
  @observable temperature: string;
  @observable enableSyntaxHighlighting: boolean;
  @observable thinkMode: string;
  @observable enableMessageCollapsing: boolean;
  @observable contextLength: string | null;
  @observable predictLength: string | null;
  @observable whisperLanguage: string = 'auto';
  
  constructor(props: ChatConfigTabProps) {
    super(props);

    const chat = props.chat;
    this.temperature = chat.temperature?.toFixed(1) || '0.8';
    this.enableSyntaxHighlighting = chat.enableSyntaxHighlighting ?? true;
    this.thinkMode = chat.thinkMode ?? 'medium';
    this.enableMessageCollapsing = !chat.showToolCalls;
    this.contextLength = chat.contextLength ?? null;
    this.predictLength = chat.predictLength ?? null;
    this.whisperLanguage = chat.whisperLanguage || 'auto';
    
    makeObservable(this);
  }

  componentDidMount(): void {
  }

  componentWillUnmount(): void {
  }

  render(){
    return (
      <>
        <div className="form-group">
          <ToggleFormButton
            label="Enable code syntax highlighting"
            checked={this.enableSyntaxHighlighting}
            onChange={this._onEnableSyntaxHighlightingChange}
          />
        </div>
        <div className="form-section">
          <div className="form-group">
            <ToggleFormButton
              label="Show tool calls"
              checked={!this.enableMessageCollapsing}
              onChange={this._onMessageCollapsingChange}
            />
          </div>
        </div>
        <div className="form-section">
          <div className="form-group">
              <label className="form-label">Thinking mode</label>
              <select
                className="form-input"
                defaultValue={this.thinkMode}
                onChange={this._onThinkingModeChange}
              >
              <option value="off">Off</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              </select>
          </div>
          <div className="form-group">
            <label className="form-label">Temperature</label>
            <select className="form-input" value={this.temperature} onChange={this._onTemperatureChange}>
              <option value="0.1">Conservative (0.1)</option>
              <option value="0.5">Balanced (0.5)</option>
              <option value="0.8">Creative (0.8)</option>
              <option value="1.0">Very Creative (1.0)</option>
            </select>
          </div>
        </div>
        <div className="form-section">
          <div className="form-group">
            <label className="form-label">Audio language (Whisper)</label>
            <select className="form-input" value={this.whisperLanguage} onChange={this._onWhisperLanguageChange}>
              {WHISPER_LANGUAGES.map(lang => (
                <option key={lang.code} value={lang.code}>{lang.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="form-group">
          <ContextLengthSlider contextLength={this.contextLength ? this.contextLength.toString() : 'auto'} onContextLengthChange={this._onContextLengthChange} />
        </div>
        <div className="form-group">
          <div className="context-length-slider">
            <div className="slider-header">
              <label className="slider-label">Max Output Tokens (num_predict)</label>
              <span className="slider-value">{this.predictLength ?? 'auto'}</span>
            </div>
            <div className="slider-container">
              <input
                type="range"
                min="0"
                max={PREDICT_OPTIONS.length - 1}
                value={PREDICT_OPTIONS.indexOf(this.predictLength ?? 'auto') !== -1 ? PREDICT_OPTIONS.indexOf(this.predictLength ?? 'auto') : PREDICT_OPTIONS.length - 1}
                onChange={this._onPredictLengthChange}
                className="slider-track"
              />
              <div className="slider-labels">
                {PREDICT_OPTIONS.map((option, index) => (
                  <span
                    key={index}
                    className="slider-label-text"
                    style={{ left: `${(index / (PREDICT_OPTIONS.length - 1)) * 100}%` }}
                  >
                    {option}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </>      
    );
  }

  private _notifySettingsChanged() {
    this.props.onSettingsChanged({
      temperature: parseFloat(this.temperature),
      enableSyntaxHighlighting: this.enableSyntaxHighlighting,
      thinkMode: this.thinkMode,
      showToolCalls: !this.enableMessageCollapsing,
      contextLength: this.contextLength,
      predictLength: this.predictLength,
      whisperLanguage: this.whisperLanguage
    });
  }

  @action _onTemperatureChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    this.temperature = e.target.value;
    
    this._notifySettingsChanged();
  }
  
  @action
  private _onThinkingModeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    this.thinkMode = e.target.value;

    this._notifySettingsChanged();
  }

  @action
  private _onEnableSyntaxHighlightingChange = (checked: boolean) => {
    this.enableSyntaxHighlighting = checked;

    // notify other components about code highlighting change
    document.dispatchEvent(new CustomEvent('enableSyntaxHighlightingChanged', { detail: this.enableSyntaxHighlighting }));

    this._notifySettingsChanged();
  }

  @action
  private _onMessageCollapsingChange = (checked: boolean) => {
    this.enableMessageCollapsing = !checked;
    localStorage.setItem('enableMessageCollapsing', String(this.enableMessageCollapsing));
    // notify other components about message collapsing change
    document.dispatchEvent(new CustomEvent('messageCollapsingChanged', { detail: this.enableMessageCollapsing }));

    this._notifySettingsChanged();
  }
  
  @action
  private _onWhisperLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    this.whisperLanguage = e.target.value;
    localStorage.setItem('whisperLanguage', this.whisperLanguage);

    this._notifySettingsChanged();
  }

  @action
  private _onContextLengthChange = (contextLength: string) => {
    this.contextLength = contextLength === 'auto' ? null : contextLength;
    localStorage.setItem('contextLength', contextLength);

    this._notifySettingsChanged();
  }

  @action
  private _onPredictLengthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const idx = parseInt(e.target.value, 10);
    const value = PREDICT_OPTIONS[idx];
    this.predictLength = value === 'auto' ? null : value;
    if (this.predictLength) localStorage.setItem('predictLength', this.predictLength);
    else localStorage.removeItem('predictLength');

    this._notifySettingsChanged();
  }
}

export default ChatConfigTab;